import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { adminApi } from '../adminApi';

interface AdminUserRow {
  id: string;
  email: string;
  tier: 'free' | 'pro' | 'enterprise';
  email_verified: boolean;
  created_at: string;
  last_activity_at: string | null;
  boat_count: number;
}

function formatDate(iso: string | null): string {
  if (iso === null) return '—';
  return new Date(iso).toLocaleString();
}

export function AdminUsers() {
  const location = useLocation();
  const [users, setUsers] = useState<AdminUserRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const notice =
    typeof (location.state as { notice?: unknown } | null)?.notice === 'string'
      ? (location.state as { notice: string }).notice
      : null;

  useEffect(() => {
    void (async () => {
      try {
        const res = await adminApi<{ users: AdminUserRow[] }>('/v1/admin/users');
        setUsers(res.users);
      } catch (err) {
        const e = err as { body?: { message?: string } };
        setError(e.body?.message ?? 'Failed to load users.');
      }
    })();
  }, []);

  return (
    <section className="admin-section">
      <header className="admin-section-header">
        <h1>Users</h1>
        <span className="admin-count">{users === null ? '…' : `${users.length} total`}</span>
      </header>

      {notice !== null ? (
        <div className="admin-auth-banner" role="status" aria-live="polite">
          {notice}
        </div>
      ) : null}
      {error !== null ? <div className="admin-auth-banner error">{error}</div> : null}

      {users === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}

      {users !== null ? (
        <div className="scroll-x">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Tier</th>
                <th>Verified</th>
                <th>Boats</th>
                <th>Created</th>
                <th>Last activity</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
                  <td>
                    <Link to={`/admin/users/${u.id}`}>{u.email}</Link>
                  </td>
                  <td>{u.tier}</td>
                  <td>{u.email_verified ? 'Yes' : 'No'}</td>
                  <td>{u.boat_count}</td>
                  <td>{formatDate(u.created_at)}</td>
                  <td>{formatDate(u.last_activity_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
